import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Icon, Tooltip } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import cx from 'classnames';
import { defaultProductName } from 'Core/defaultValues';
import AppIcon from '../appIcon';
import TransmissionLog from '../transmissionLog';
import { getProductName } from '../../constants';
import { translateEvent } from 'Modules/.common/utils/translateEvents';
import { CONSOLE_POLL_MS, FRESH_VEHICLE_MS, httpStatusText, latestTransmission, linkState, relativeTime, sinceText, telemetryState, todayByUnit, zoneLabel } from '../../transmissions';
// Actions
import { accountAppsSearchUnits, accountAppsTransmissionStats, openAppsConsole, openAppsLog } from '../../redux/slice';
import './styles.scss';

const FILTERS = ['linked', 'unmatched', 'all'];
const PILL_ICONS = { linked: 'link', unmatched: 'link_off', inactive: 'pause_circle' };

/**
 * Console of the vehicles an integration synced (design "CoastPay linked vehicles", proposal B): the link with
 * the app, four KPIs and the vehicles table with each one's last transmission and today's count. It is a live
 * view by polling: the daily summaries refresh while it is open; the Apps tab reloads the vehicles when the
 * sync push arrives. Data: searchCompanyIntegrationUnits and getCompanyIntegrationTransmissionStats (main API).
 */
const LinkedVehiclesConsole = ({ intl: { messages }, integration }) => {
    const dispatch = useDispatch();
    const { units, unitsLoading, unitsError, stats, log } = useSelector((state) => state.accountAppsRedux);
    const reseller = useSelector((state) => state.resellerRedux?.current_reseller);
    const productName = getProductName(reseller, defaultProductName);
    // The product side of the link pill: the brand flame, or a neutral hub on white-label accounts
    const productIcon = productName === defaultProductName ? 'local_fire_department' : 'hub';
    const [filter, setFilter] = useState('linked');
    const [search, setSearch] = useState('');
    const [hoverId, setHoverId] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    const integrationId = integration.id;
    const appName = integration.integrationName;
    // Translation of this module with the app, product and extra placeholders replaced
    const tr = (key, values = {}) => Object.entries({ _APP_: appName, _PRODUCT_: productName, ...values })
        .reduce((text, [placeholder, value]) => text.split(placeholder).join(value), messages[key] || '');

    const loadUnits = () => dispatch(accountAppsSearchUnits(integrationId));
    const loadStats = () => dispatch(accountAppsTransmissionStats({ companyIntegrationId: integrationId }));
    useEffect(() => {
        loadUnits();
        loadStats();
        const clock = setInterval(() => setNow(Date.now()), 1000);
        const poll = setInterval(loadStats, CONSOLE_POLL_MS);
        return () => {
            clearInterval(clock);
            clearInterval(poll);
        };
    }, [integrationId]);

    const close = () => dispatch(openAppsConsole(null));
    const openLog = (options = {}) => dispatch(openAppsLog({ open: true, ...options }));

    const timeZone = stats?.timeZone;
    const vehicles = units || [];
    const today = useMemo(() => todayByUnit(stats), [stats]);
    const counts = useMemo(() => {
        const result = { linked: 0, unmatched: 0, inactive: 0, all: vehicles.length };
        vehicles.forEach((unit) => { result[linkState(unit)] += 1; });
        return result;
    }, [units]);
    const query = search.trim().toLowerCase();
    const rows = useMemo(() => vehicles.filter((unit) => (filter === 'all' || linkState(unit) === filter)
        && (!query || [unit.unitLabel, unit.providerName, unit.vin, unit.licensePlate].some((text) => (text || '').toLowerCase().includes(query)))),
    [units, filter, query]);

    const summaries = Object.values(today);
    const countOf = (item) => (item.sent || 0) + (item.errors || 0);
    const todayMax = Math.max(1, ...summaries.map(countOf));
    const errorsToday = summaries.reduce((sum, item) => sum + (item.errors || 0), 0);
    const transmissionsToday = summaries.reduce((sum, item) => sum + countOf(item), 0);
    const latest = latestTransmission(stats?.items);

    // Live transmission of an app with telemetry, from the daily summaries this console polls: live (a transmission in
    // the last minutes), idle (older ones), none yet; paused while the integration is Inactive (data-catalog sends only
    // for active integrations). Apps without telemetry have no live state.
    const active = integration.status === 'Active';
    const showLive = !!integration.telemetry;
    const liveState = !active ? 'paused' : telemetryState({ lastOn: latest?.lastOn || null }, now);
    const live = liveState === 'live';
    const liveLabel = messages[live ? 'accountAppsConsole_live' : liveState === 'paused' ? 'accountAppsConsole_paused' : 'accountAppsConsole_idle'];
    const liveSub = liveState === 'paused' ? messages['accountAppsConsole_pausedInactive']
        : liveState === 'none' ? messages['accountAppsConsole_liveWaiting']
            : tr(live ? 'accountAppsConsole_liveLast' : 'accountAppsConsole_idleLast', { _TIME_: sinceText(latest.lastOn, now, timeZone, messages) });

    const kpis = [
        { key: 'linked', label: messages['accountAppsConsole_kpiLinked'], value: counts.linked, tip: tr('accountAppsConsole_kpiLinkedTip'), onClick: () => setFilter('linked'), selected: filter === 'linked' },
        { key: 'unmatched', label: messages['accountAppsConsole_kpiUnmatched'], value: counts.unmatched, tip: tr('accountAppsConsole_kpiUnmatchedTip'), onClick: () => setFilter('unmatched'), selected: filter === 'unmatched' },
        { key: 'errors', label: messages['accountAppsConsole_kpiErrors'], value: errorsToday, tip: messages['accountAppsConsole_kpiErrorsTip'], onClick: () => openLog({ errorsOnly: true }) },
        { key: 'today', label: messages['accountAppsConsole_kpiToday'], value: transmissionsToday.toLocaleString(), tip: tr('accountAppsConsole_kpiTodayTip', { _TZ_: zoneLabel(timeZone) }) }
    ];

    const renderRow = (unit) => {
        const state = linkState(unit);
        const linked = state === 'linked';
        const summary = linked ? today[unit.unitId] : null;
        const last = summary?.lastOn || null;
        const lastError = summary?.lastStatus === 'ERROR';
        const fresh = !!(linked && last && now - Date.parse(last) < FRESH_VEHICLE_MS);
        const count = summary ? countOf(summary) : 0;
        const data = unit.providerData || {};
        const model = [data.make, data.model, data.modelYear].filter(Boolean).join(' ');
        const lastEvent = !linked ? messages['accountAppsConsole_notTransmitted']
            : !last ? messages['accountAppsConsole_waitingFirst']
                : lastError ? `${messages['accountAppsConsole_lastFailed']}${summary.lastHttpStatus ? ` · ${httpStatusText(summary.lastHttpStatus)}` : ''}`
                    : translateEvent(summary.lastEvent, messages) || messages['accountAppsLog_eventDefault'];
        const logTip = tr('accountAppsConsole_rowLogTip', { _UNIT_: unit.unitLabel || unit.providerName || '' });
        return (
            <div
                key={unit.id}
                className={cx('console-row', { 'is-fresh': fresh })}
                role="row"
                onMouseEnter={() => setHoverId(unit.id)}
                onMouseLeave={() => setHoverId(null)}
            >
                <Tooltip title={unit.unitLabel || ''} placement="top">
                    <div className={cx('console-unit', { 'is-muted': !unit.unitId })}>{unit.unitLabel || messages['accountAppsConsole_notMatched']}</div>
                </Tooltip>
                <div className="console-vehicle">
                    <Tooltip title={unit.providerName || ''} placement="top">
                        <div className="console-vehicle-name">{unit.providerName || '—'}</div>
                    </Tooltip>
                    <div className="console-vehicle-sub">{[unit.vin, model].filter(Boolean).join(' · ')}</div>
                </div>
                <div>
                    <Tooltip title={tr(`accountAppsConsole_link_${state}Tip`)} placement="top">
                        <span className={cx('console-pill', `is-${state}`)}>
                            <Icon className="console-pill-icon">{PILL_ICONS[state]}</Icon>
                            {messages[`accountAppsConsole_link_${state}`]}
                        </span>
                    </Tooltip>
                </div>
                <div className="console-last">
                    <span className={cx('console-last-dot', { 'is-off': !linked, 'is-error': linked && lastError, 'is-fresh': fresh && live })} />
                    <span className="console-last-text">
                        <span className="console-last-time">{linked && last ? relativeTime(last, now, timeZone, messages) : '—'}</span>
                        <span className="console-last-event">{lastEvent}</span>
                    </span>
                </div>
                <div className="console-today">
                    <span className="console-today-count">{linked ? count : '—'}</span>
                    <span className="console-today-bar"><span style={{ width: `${linked ? Math.round(count / todayMax * 100) : 0}%` }} /></span>
                </div>
                <div className="console-row-actions">
                    {
                        linked &&
                        <Tooltip title={logTip} placement="top">
                            <button
                                type="button"
                                className={cx('console-row-log', { 'is-visible': hoverId === unit.id })}
                                aria-label={logTip}
                                onClick={() => openLog({ unitId: unit.unitId })}
                            >
                                <ReceiptLongOutlinedIcon />
                            </button>
                        </Tooltip>
                    }
                </div>
            </div>
        );
    };

    const firstLoad = units === null;
    return (
        <Dialog
            open
            onClose={close}
            maxWidth={false}
            className="account-apps-console"
            PaperProps={{ className: 'account-apps-console-paper', 'aria-label': tr('accountAppsConsole_title') }}
        >
            <header className="console-header">
                <div className="console-heading">
                    <AppIcon className="console-app-icon" glyphClassName="console-app-glyph" icon={integration.icon} color={integration.color} name={appName} />
                    <div className="console-heading-text">
                        <div className="console-title">{tr('accountAppsConsole_title')}</div>
                        <div className="console-sub">{messages['accountAppsConsole_sub']}</div>
                    </div>
                </div>
                {
                    showLive &&
                    <div className={cx('console-link', { 'is-live': live })}>
                        <span className="console-link-node console-link-product"><Icon className="console-link-product-icon">{productIcon}</Icon></span>
                        <span className="console-link-line" />
                        <AppIcon className="console-link-node console-link-app" glyphClassName="console-link-glyph" icon={integration.icon} color={integration.color} name={appName} />
                        <span className="console-link-state">
                            <span className="console-link-dot" />
                            <strong>{liveLabel}</strong>
                            <span className="console-link-sub">{liveSub}</span>
                        </span>
                    </div>
                }
                <div className="console-actions">
                    <Tooltip title={messages['accountAppsConsole_viewLog']} placement="bottom">
                        <button type="button" className="console-log-button" aria-label={messages['accountAppsConsole_viewLog']} onClick={() => openLog()}>
                            <ReceiptLongOutlinedIcon />
                        </button>
                    </Tooltip>
                    <Tooltip title={messages['accountAppsConsole_close']} placement="bottom">
                        <button type="button" className="console-close" aria-label={messages['accountAppsConsole_close']} onClick={close}>
                            <Icon>close</Icon>
                        </button>
                    </Tooltip>
                </div>
            </header>

            <div className="console-body">
                {firstLoad && unitsLoading && <div className="console-loading"><Icon className="console-spin">autorenew</Icon></div>}
                {
                    firstLoad && !unitsLoading && unitsError &&
                    <div role="alert" className="console-error">
                        {messages['accountAppsConsole_loadError']}
                        <button type="button" onClick={() => { loadUnits(); loadStats(); }}>{messages['accountAppsWizard_retry']}</button>
                    </div>
                }
                {
                    !firstLoad &&
                    <>
                        <div className="console-kpis">
                            {kpis.map((kpi) => (
                                <Tooltip key={kpi.key} title={kpi.tip} placement="top">
                                    <button
                                        type="button"
                                        className={cx('console-kpi', `is-${kpi.key}`, { 'is-selected': kpi.selected, 'is-static': !kpi.onClick })}
                                        onClick={kpi.onClick}
                                    >
                                        <span className="console-kpi-label"><span className="console-kpi-dot" />{kpi.label}</span>
                                        <span className="console-kpi-value">{kpi.value}</span>
                                    </button>
                                </Tooltip>
                            ))}
                        </div>

                        <section className="console-card">
                            <div className="console-card-head">
                                <div className="console-card-title">{messages['accountAppsConsole_vehicles']}</div>
                                <div className="console-filters">
                                    {FILTERS.map((key) => (
                                        <button key={key} type="button" className={cx('console-chip', { 'is-selected': filter === key })} aria-pressed={filter === key} onClick={() => setFilter(key)}>
                                            {messages[`accountAppsConsole_filter_${key}`]} · {counts[key]}
                                        </button>
                                    ))}
                                </div>
                                <div className="console-search">
                                    <input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={messages['accountAppsConsole_search']}
                                        aria-label={messages['accountAppsConsole_search']}
                                    />
                                    <Icon className="console-search-icon">search</Icon>
                                </div>
                            </div>
                            <div className="console-table-scroll">
                                <div className="console-table" role="table">
                                    <div className="console-row console-row-head" role="row">
                                        <div>{messages['accountAppsConsole_colUnit']}</div>
                                        <div>{tr('accountAppsConsole_colVehicle')}</div>
                                        <div>{messages['accountAppsConsole_colLink']}</div>
                                        <div>{messages['accountAppsConsole_colLast']}</div>
                                        <div className="console-today">{messages['accountAppsConsole_colToday']}</div>
                                        <div />
                                    </div>
                                    {rows.map(renderRow)}
                                    {
                                        !rows.length &&
                                        <div className="console-empty">
                                            <Icon className="console-empty-icon">{vehicles.length ? 'search_off' : 'local_shipping'}</Icon>
                                            <div className="console-empty-title">{vehicles.length ? messages['accountAppsConsole_noMatch'] : messages['accountAppsConsole_noVehicles']}</div>
                                            {
                                                vehicles.length > 0 &&
                                                <button type="button" onClick={() => { setSearch(''); setFilter('all'); }}>{messages['accountAppsConsole_clearFilters']}</button>
                                            }
                                        </div>
                                    }
                                </div>
                            </div>
                        </section>
                    </>
                }
            </div>

            {log.open && <TransmissionLog integration={integration} units={vehicles} stats={stats} />}
        </Dialog>
    );
};

export default injectIntl(LinkedVehiclesConsole);
